import { useState, useEffect, useCallback, useMemo } from 'react';
import { notification, Button, Space, Tag } from 'antd';
import { FormOutlined, UserSwitchOutlined } from '@ant-design/icons';
import "./UsersList.scss";
import service from '../../services/Service';
import { hasPrivilege } from '../../api/token';
import { withLocale } from '../../l10n';
import { impersonate } from "../../api/user";
import DataTable from '../DataTable/DataTable';
import DeleteButton from '../DeleteButton/DeleteButton';
import withNavigation from '../hooks/withNavigation.jsx';
import { GetColumnLiteralSearchProps } from '../../utils/functions.jsx';
import { useGetTenantsLookupQuery, useGetRolesLookupQuery } from '../../services/lookupApi.js';

const EMPTY_ARRAY = [];

function UsersList({ lang, navigate }) {

  // ===== 1. ESTADOS =====
  const [ui, setUI] = useState({
    loading: false,
  });

  const [data, setData] = useState({
    raw: [],
    selected: null,
  });

  // ===== 2. SELECTORES =====
  const { data: tenants = EMPTY_ARRAY } = useGetTenantsLookupQuery();
  const { data: roles = EMPTY_ARRAY } = useGetRolesLookupQuery();

  // ===== 3. DERIVADOS (Computados) =====
  const rolesFilter = roles.map(role => ({
    text: role.name,
    value: role.uuid
  }));

  const tenantsFilter = tenants.map(tenant => ({
    text: tenant.name,
    value: tenant.uuid
  }));

  // ===== 4. HELPERS (Para actualizar estado) =====
  const updateUI = (field, value) => setUI(prev => ({ ...prev, [field]: value }));
  const updateData = (field, value) => setData(prev => ({ ...prev, [field]: value }));

  // ===== 5. FUNCIONES PRINCIPALES =====
  const Pfetch = useCallback(async () => {
    updateUI('loading', true);
    try {
      const res = await service.get('users');

      if (!res || res.data.ok === false) {
        notification["error"]({
          message: lang.handle(res?.data?.message || "Error al obtener datos"),
        });
        updateData('raw', []);
      } else {
        updateData('raw', res.data.data || []);
      }
    } catch (err) {
      console.error("Error devuelto: " + err);
    } finally {
      updateUI('loading', false);
    }
  }, [lang]);

  const enrichedData = useMemo(() => {
    return data.raw.map((element) => {
      const tenant = tenants.find((item) => item.uuid === element.tenant_uuid);
      return {
        ...element,
        tenant_name: (element.tenant_uuid && tenant)
          ? tenant.name
          : lang.handle("Sin organización")
      };
    });
  }, [data.raw, tenants, lang]);

  // ===== 6. HANDLERS =====
  const handleDeleteUser = async () => {
    if (!data.selected) return;

    updateUI('loading', true);
    try {
      const res = await service.del(
        "users/" + data.selected.uuid,
        data.selected.uuid
      );

      if (res.data.ok === false) {
        notification["error"]({
          message: res.data.message,
        });
      } else {
        notification["success"]({
          message: lang.handle('Usuario borrado correctamente'),
        });

        try {
          const resVpn = await service.del("vpn/" + data.selected.username);
          if (resVpn.data.ok === false && resVpn.data.message) {
            notification["error"]({
              message: lang.handle(resVpn.data.message),
            });
          } else {
            notification["success"]({
              message: lang.handle('VPN actualizada correctamente'),
            });
          }
        } catch (vpnErr) {
          console.error("Error VPN: " + vpnErr);
        }
      }
      
      updateData('selected', null);
      await Pfetch();
    } catch (err) {
      console.error("Error devuelto: " + err);
    } finally {
      updateUI('loading', false);
    }
  };

  const handleImpersonateUser = async () => {
    if (!data.selected) return;
    const result = await impersonate(data.selected.uuid);
    if (!result.ok) {
      notification["error"]({
        message: lang.handle(result.message),
      });
    }
  };

  const handleUpdateUser = () => {
    if (!data.selected) return;
    navigate(`/users/edit/${data.selected.uuid}`);
  };

  const getRoleTag = (roleUuid) => {
    const role = roles.find(el => el.uuid === roleUuid);
    return role ? role.name : roleUuid;
  };

  // ===== 7. EFFECTS =====
  useEffect(() => {
    updateUI('loading', true);
    Pfetch();
  }, [Pfetch]);

  // ===== 8. CONFIG (columnas, etc) =====
  const onSelectionChange = (selectedRowKeys, selectedRows) => {
    updateData('selected', selectedRows.length > 0 ? selectedRows[0] : null);
  };

  const columns = [
    {
      title: lang.handle("Nombre de usuario"),
      dataIndex: 'username',
      ...GetColumnLiteralSearchProps('username', lang),
    },
    {
      title: lang.handle('Rol'),
      dataIndex: 'role_uuid',
      filters: rolesFilter,
      onFilter: (value, record) => record.role_uuid === value,
      render: (value) => <Tag>{getRoleTag(value)}</Tag>,
    },
    {
      title: lang.handle('Organización'),
      dataIndex: 'tenant_name',
      filters: tenantsFilter,
      onFilter: (value, record) => record.tenant_uuid === value,
    },
    {
      title: lang.handle('Nombre'),
      dataIndex: 'name',
      ...GetColumnLiteralSearchProps('name', lang),
    },
    {
      title: lang.handle('Apellidos'),
      dataIndex: 'lastname',
      ...GetColumnLiteralSearchProps('lastname', lang),
    },
    {
      title: lang.handle('Email'),
      dataIndex: 'email',
      ...GetColumnLiteralSearchProps('email', lang),
    },
  ];

  // ===== 9. RENDER =====
  return (
    <div>
      <DataTable
        rowSelection={{
          type: "radio",
          selectedRowKeys: data.selected?.uuid ? [data.selected.uuid] : [],
          onChange: onSelectionChange,
        }}
        columns={columns}
        dataSource={enrichedData}
        rowKey="uuid"
        loading={ui.loading}
      />
      <div className="button-panel">
        <Space>
          {hasPrivilege('users_edit') && (
            <Button
              type="primary"
              icon={<FormOutlined />}
              disabled={!data.selected}
              onClick={handleUpdateUser}
            >
              {lang.handle('Editar')}
            </Button>
          )}

          {hasPrivilege('users_delete') && (
            <DeleteButton
              onEvent={handleDeleteUser}
              buttonDisabled={!data.selected}
            />
          )}

          {hasPrivilege('impersonate') && (
            <Button
              type="primary"
              icon={<UserSwitchOutlined />}
              disabled={!data.selected}
              onClick={handleImpersonateUser}
            >
              {lang.handle('Personificar')}
            </Button>
          )}
        </Space>
      </div>
    </div>
  );
};

export default withLocale(withNavigation(UsersList));
